import { memo, useCallback } from 'react';
import { shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { REQUESTS_TABS } from '~/constants';
import { clanIsFull } from '~/helpers/clan';
import { showRulesDialog, showSendApplicationDialog } from '~/helpers/dialogs';
import { hasPermission, PERMISSIONS } from '~/roles';
import { useAppDispatch, useAppSelector } from '~/store';
import { ENTRY_SOURCE } from '~/store/slices/settingsSlice';
import { declineApplicationsThunk } from '~/store/thunks/applicationsThunks';
import { actionsApplications } from '~/Actions/ActionApplications';

import NonOperationalEntries from '~/UIKit/Entries/NonOperationalEntries';
import OperationalEntries from '~/UIKit/Entries/OperationalEntries';

import type { IClanApplication } from '~/Actions/ActionApplications';
import type { ClanType, IAccountInvite } from '~/Actions/ActionInvites';
import type { IApplicationsState } from '~/Reducers/ReducerApplications';
import type { IInvitesState } from '~/Reducers/ReducerInvites';
import type { RootState } from '~/store';

const stateSelector = (state: RootState) => {
  const clanId = state.currentAccount.clanId;
  const currentClan = clanId ? state.clans.items[clanId] : undefined;

  return {
    currentAccount: state.currentAccount,
    currentClan,
    clanLoaded: !!currentClan,
    isCurrentClanFull: !!currentClan && clanIsFull(currentClan),
    selectedApplicationsPlayersIds: state.applications.selectedApplicationsPlayersIds,
  };
};

type Headers = Array<{
  text: string;
  isSortable: boolean;
  name: string;
  modify: string;
  glyph?: string;
}>;

type BaseProps = {
  isOperational: boolean;
  headers: Headers;

  fetchEntriesByCurrentState: (withGlobalSpinner: boolean) => void;
  fetchEntriesByPage: (page: number) => void;
  onReloadClick: () => void;
  onSortClick: (order: string, isAsc: boolean) => void;
};

type OperationalInvitesProps = BaseProps & {
  isOperational: true;
  entryType: REQUESTS_TABS.INVITES;
  entries: IInvitesState;

  onAcceptedEntry: (invite: IAccountInvite) => void;
  onDeclinedEntry: (invite: IAccountInvite) => void;
};

type OperationalApplicationsProps = BaseProps & {
  isOperational: true;
  entryType: REQUESTS_TABS.APPLICATION;
  entries: IApplicationsState;

  onAcceptedEntry: (application: IClanApplication) => void;
  onDeclinedEntry: (application: IClanApplication) => void;
};

type NonOperationalInvitesProps = BaseProps & {
  isOperational: false;
  entryType: REQUESTS_TABS.INVITES;
  entries: IInvitesState;
};

type NonOperationalApplicationsProps = BaseProps & {
  isOperational: false;
  entryType: REQUESTS_TABS.APPLICATION;
  entries: IApplicationsState;
};

type EntriesProps =
  | OperationalInvitesProps
  | OperationalApplicationsProps
  | NonOperationalInvitesProps
  | NonOperationalApplicationsProps;

const Entries = (props: EntriesProps) => {
  const {
    isOperational,
    entries,
    entryType,
    headers,
    fetchEntriesByCurrentState,
    fetchEntriesByPage,
    onReloadClick,
    onSortClick,
  } = props;

  const { currentAccount, currentClan, clanLoaded, isCurrentClanFull, selectedApplicationsPlayersIds } = useAppSelector(
    stateSelector,
    shallowEqual,
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const dropSelection = useCallback(() => {
    dispatch(actionsApplications.dropSelection());
  }, [dispatch]);

  const onApplicationsPlayerTick = useCallback(
    (playerId: number) => {
      dispatch(actionsApplications.toggleApplicationsPlayerTick(playerId));
    },
    [dispatch],
  );

  const onAllApplicationsPlayersTick = useCallback(
    (playerIds: number[]) => {
      dispatch(actionsApplications.toggleAllApplicationsPlayersTick(playerIds));
    },
    [dispatch],
  );

  const sendApplication = useCallback(
    (clan: ClanType) => {
      const shouldFetchApplications = true;
      dispatch(showSendApplicationDialog(clan, ENTRY_SOURCE.RECRUITSTATION, shouldFetchApplications));
    },
    [dispatch],
  );

  const onDeclineApplications = useCallback(() => {
    void dispatch(declineApplicationsThunk());
  }, [dispatch]);

  const onRulesDialogClick = useCallback(() => {
    dispatch(showRulesDialog());
  }, [dispatch]);

  if (currentAccount.clanId && !(clanLoaded && hasPermission(currentAccount.roleName, PERMISSIONS.HANDLE_INVITES))) {
    return null;
  }

  // Operational
  if (isOperational) {
    if (entryType === REQUESTS_TABS.INVITES) {
      return (
        <OperationalEntries
          currentAccount={currentAccount}
          entries={entries}
          entryType={entryType}
          headers={headers}
          navigate={navigate}
          fetchEntriesByCurrentState={fetchEntriesByCurrentState}
          fetchEntriesByPage={fetchEntriesByPage}
          onAcceptedEntry={props.onAcceptedEntry}
          onDeclinedEntry={props.onDeclinedEntry}
          onReloadClick={onReloadClick}
          onSortClick={onSortClick}
          onRulesDialogClick={onRulesDialogClick}
        />
      );
    }

    return (
      <OperationalEntries
        currentAccount={currentAccount}
        clan={currentClan!}
        entries={entries}
        entryType={entryType}
        headers={headers}
        navigate={navigate}
        fetchEntriesByCurrentState={fetchEntriesByCurrentState}
        fetchEntriesByPage={fetchEntriesByPage}
        onAcceptedEntry={props.onAcceptedEntry}
        onDeclinedEntry={props.onDeclinedEntry}
        onReloadClick={onReloadClick}
        onSortClick={onSortClick}
        dropSelection={dropSelection}
        selectedApplicationsPlayersIds={selectedApplicationsPlayersIds}
        onAllApplicationsPlayersTick={onAllApplicationsPlayersTick}
        onApplicationsPlayerTick={onApplicationsPlayerTick}
        onDeclineApplications={onDeclineApplications}
      />
    );
  }

  // Non-operational
  if (entryType === REQUESTS_TABS.INVITES) {
    return (
      <NonOperationalEntries
        currentAccount={currentAccount}
        entries={entries}
        entryType={entryType}
        headers={headers}
        isCurrentClanFull={isCurrentClanFull}
        navigate={navigate}
        fetchEntriesByCurrentState={fetchEntriesByCurrentState}
        fetchEntriesByPage={fetchEntriesByPage}
        onReloadClick={onReloadClick}
        onSortClick={onSortClick}
      />
    );
  }

  return (
    <NonOperationalEntries
      currentAccount={currentAccount}
      entries={entries}
      entryType={entryType}
      headers={headers}
      navigate={navigate}
      fetchEntriesByCurrentState={fetchEntriesByCurrentState}
      fetchEntriesByPage={fetchEntriesByPage}
      sendApplication={sendApplication}
      onReloadClick={onReloadClick}
      onSortClick={onSortClick}
    />
  );
};

export default memo(Entries);
